import { Link } from '@tanstack/react-router'

import { buttonVariants } from '@/ui/button-variants'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { StatusBadge } from '@/ui/status-badge'
import { useCanEditSettings } from '../../lib/setting-queries'
import { useAssistantPolicyResult } from './assistant-policy-queries'

export function AssistantPolicySection({ entityId }: { entityId: string }) {
  const canEdit = useCanEditSettings(entityId)
  const resultQuery = useAssistantPolicyResult(entityId, true)
  const result = resultQuery.data
  const isCurrent = !resultQuery.isError && result?.refreshStatus === 'Current'
    && result.selectionStatus === 'Evaluated' && result.value !== null
  const refreshing = result?.refreshStatus === 'Pending' || result?.refreshStatus === 'Running'
  const failed = result?.refreshStatus === 'Failed'
  const currentLabel = result?.value === true ? 'Enabled' : 'Disabled'
  let refreshMessage = 'Policy selection has not completed. The assistant is temporarily unavailable.'
  if (refreshing) refreshMessage = 'Policy selection is refreshing. The assistant is temporarily unavailable.'
  if (failed) refreshMessage = 'Policy refresh failed. The assistant is unavailable until policy selection refreshes.'

  return (
    <section className="flex flex-col gap-6" aria-labelledby="assistant-settings-title">
      <header className="flex flex-col gap-1">
        <h2 id="assistant-settings-title" className="text-foreground text-base font-medium">AI assistant</h2>
        <p className="text-muted-foreground text-sm">
          Policies control the assistant for this entity only. Subentities keep their own policies.
          A platform-wide control can also turn it off.
        </p>
      </header>
      {resultQuery.isError && (
        <RetryNotice message="Couldn’t load the assistant policy result." onRetry={() => void resultQuery.refetch()} />
      )}
      {result === undefined && !resultQuery.isError && <LoadingNotice message="Loading assistant policy…" />}
      {result && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Assistant policy</span>
            <StatusBadge tone={isCurrent ? 'neutral' : 'warning'}>
              {isCurrent ? currentLabel : 'Unavailable'}
            </StatusBadge>
          </div>
          {result.value !== null && (
            <p className="text-muted-foreground text-sm">
              {isCurrent ? 'Value' : 'Last evaluated value'}: {result.value ? 'Enabled' : 'Disabled'}
              {result.isDefault ? ' (platform default)' : ' (selected policy)'}.
            </p>
          )}
          {!isCurrent && (
            <p role="status" className="text-muted-foreground text-sm">
              {refreshMessage}
            </p>
          )}
          {result.failureCode && <p role="alert" className="text-destructive text-sm">Refresh error: {result.failureCode}</p>}
        </div>
      )}
      <div>
        <Link to="/$entityId/settings/policies" params={{ entityId }} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
          {canEdit ? 'Manage assistant policies' : 'View assistant policies'}
        </Link>
      </div>
    </section>
  )
}
